import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AuroraMinTemperatureLimitControl from './AuroraMinTemperatureLimitControl'
import { AuroraAssetGroup, AuroraSetMinTemperatureLimitActionInput } from '../types'
import { useAuroraStack } from '../hooks/aurora'
import { publishAuroraAwhpAction } from '../services/aurora'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupMinTemperatureLimitControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (input: AuroraSetMinTemperatureLimitActionInput) => void
  afterSubmit?: (value: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupMinTemperatureLimitControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupMinTemperatureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    minTemperatureLimit: initialMinTemperatureLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [minMinTemperatureLimit, setMinTemperatureLimit] = useState<number | null>(
    initialMinTemperatureLimit
  )

  async function handleSubmit(input: AuroraSetMinTemperatureLimitActionInput) {
    try {
      setSubmitting(true)

      beforeSubmit?.(input)

      await Promise.all(auroras.map((aurora) => publishAuroraAwhpAction(aurora.id, input)))

      dispatch(createSuccessNotification(t('setMinTemperatureLimitSuccess')))
      setMinTemperatureLimit(input.alarms.minTempLimit)
      afterSubmit?.(input.alarms.minTempLimit)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setMinTemperatureLimitError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraMinTemperatureLimitControl
      value={minMinTemperatureLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
